/**
 * Page de profil utilisateur permettant de modifier les informations personnelles,
 * le mot de passe et les préférences RGPD.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { enfantService, getMediaUrl } from '../services/api';
import logger from '../utils/logger';

const Profil = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    current_password: '',  // Mot de passe actuel (requis pour les admins)
    password: '',
    password_confirm: '',
    accepts_terms: false,
    accepts_privacy_policy: false,
    accepts_data_processing: false,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [enfants, setEnfants] = useState([]);
  const [loadingEnfants, setLoadingEnfants] = useState(true);

  useEffect(() => {
    fetchUserProfile();
    if (user?.role === 'PARENT') {
      fetchEnfants();
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/users/me/');
      const userData = response.data;
      setFormData({
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        current_password: '',
        password: '',
        password_confirm: '',
        accepts_terms: userData.accepts_terms || false,
        accepts_privacy_policy: userData.accepts_privacy_policy || false,
        accepts_data_processing: userData.accepts_data_processing || false,
      });
    } catch (err) {
      logger.error('Erreur lors du chargement du profil:', err);
      setError('Impossible de charger le profil');
    }
  };

  const fetchEnfants = async () => {
    try {
      setLoadingEnfants(true);
      const response = await enfantService.getAll();
      setEnfants(response.data.results || response.data);
    } catch (err) {
      logger.error('Erreur lors du chargement des enfants:', err);
    } finally {
      setLoadingEnfants(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Réinitialiser les messages
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation des champs obligatoires
    if (!formData.first_name || !formData.last_name || !formData.email) {
      setError('Le prénom, le nom et l\'email sont obligatoires');
      setLoading(false);
      return;
    }

    // Validation des mots de passe
    if (formData.password && formData.password !== formData.password_confirm) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (formData.password && formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      setLoading(false);
      return;
    }

    // SÉCURITÉ: Validation spécifique pour les admins
    const isAdmin = user?.role === 'ADMIN';
    const emailChanged = formData.email !== user?.email;

    if (isAdmin) {
      // Si l'admin change son mot de passe, le mot de passe actuel est obligatoire
      if (formData.password && !formData.current_password) {
        setError('Pour changer votre mot de passe, vous devez saisir votre mot de passe actuel');
        setLoading(false);
        return;
      }

      // Si l'admin change son email, le mot de passe actuel est obligatoire
      if (emailChanged && !formData.current_password) {
        setError('Pour changer votre email, vous devez saisir votre mot de passe actuel');
        setLoading(false);
        return;
      }
    }

    try {
      // Préparer les données à envoyer
      const dataToSend = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        address: formData.address,
      };

      // Pour les admins, ajouter le mot de passe actuel si fourni
      if (isAdmin && formData.current_password) {
        dataToSend.current_password = formData.current_password;
      }

      // Ajouter le mot de passe uniquement s'il est fourni
      if (formData.password) {
        dataToSend.password = formData.password;
        dataToSend.password_confirm = formData.password_confirm;
      }

      const response = await api.put('/users/update_profile/', dataToSend);

      // Mettre à jour le contexte utilisateur
      if (updateUser) {
        updateUser(response.data);
      }

      setSuccess('Profil mis à jour avec succès');

      // Réinitialiser les champs de mot de passe
      setFormData(prev => ({
        ...prev,
        current_password: '',
        password: '',
        password_confirm: ''
      }));

      // Faire défiler vers le haut pour voir le message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      logger.error('Erreur lors de la mise à jour du profil:', err);
      if (err.response?.data) {
        const errors = err.response.data;
        const errorMessages = Object.entries(errors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('\n');
        setError(errorMessages || 'Erreur lors de la mise à jour du profil');
      } else {
        setError('Erreur lors de la mise à jour du profil');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await api.get('/users/export_data/', {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mes_donnees_${user.email}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSuccess('Vos données ont été exportées avec succès');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      logger.error('Erreur lors de l\'export des données:', err);
      setError('Erreur lors de l\'export de vos données');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Veuillez entrer votre mot de passe');
      return;
    }

    setDeleting(true);
    setDeleteError('');

    try {
      await api.post('/users/delete_account/', {
        password: deletePassword
      });

      // Déconnecter et rediriger
      logout();
      navigate('/login');
    } catch (err) {
      logger.error('Erreur lors de la suppression du compte:', err);
      setDeleteError(err.response?.data?.error || 'Erreur lors de la suppression du compte');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-4xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Mon Profil</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Gérez vos informations personnelles et vos préférences
            </p>
          </div>

        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            <div className="flex items-start">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-pre-line text-xs sm:text-sm">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
            <div className="flex items-start">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-xs sm:text-sm">{success}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Informations personnelles */}
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Informations personnelles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="first_name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Attention : cet email sera utilisé pour la connexion</p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Ex: 06 12 34 56 78"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="address" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Votre adresse complète"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Section Enfants - Uniquement pour les parents */}
          {user?.role === 'PARENT' && (
            <div className="border-t pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Mes enfants</h2>
                <button
                  type="button"
                  onClick={() => navigate('/enfants')}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 active:text-primary-800 font-medium transition-colors self-start sm:self-auto"
                >
                  Gérer mes enfants →
                </button>
              </div>

              {loadingEnfants ? (
                <div className="flex justify-center py-6 sm:py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : enfants.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 sm:p-6 text-center">
                  <p className="text-sm sm:text-base text-gray-600 mb-3">Aucun enfant enregistré</p>
                  <button
                    type="button"
                    onClick={() => navigate('/enfants')}
                    className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 active:text-primary-800 font-medium transition-colors"
                  >
                    + Ajouter un enfant
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {enfants.map((enfant) => (
                    <div
                      key={enfant.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer"
                      onClick={() => navigate('/enfants')}
                    >
                      <div className="flex items-start gap-3">
                        {(enfant.photo_url || enfant.photo) ? (
                          <img
                            src={enfant.photo_url || getMediaUrl(enfant.photo)}
                            alt={`${enfant.prenom} ${enfant.nom}`}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white border-2 border-white rounded-full flex items-center justify-center text-xl sm:text-2xl shadow-sm flex-shrink-0">
                            {enfant.age < 6 ? '👶' : enfant.age < 12 ? '🧒' : '👦'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                            {enfant.prenom} {enfant.nom}
                          </h3>
                          <div className="mt-1 space-y-1 text-xs text-gray-600">
                            <p className="flex items-center">
                              <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="truncate">{new Date(enfant.date_naissance).toLocaleDateString('fr-FR')}</span>
                            </p>
                            {enfant.lieu_naissance && (
                              <p className="flex items-center">
                                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{enfant.lieu_naissance}</span>
                              </p>
                            )}
                            <p className="font-medium text-gray-700">{enfant.age} ans</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Changement de mot de passe */}
          <div className="border-t pt-4 sm:pt-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Modifier le mot de passe</h2>

            {/* Message différent selon le rôle */}
            {user?.role === 'ADMIN' ? (
              <div className="mb-3 sm:mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-start">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0 text-amber-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-xs sm:text-sm text-amber-800 font-medium">Sécurité renforcée pour les administrateurs</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Pour changer votre mot de passe ou votre email, vous devez obligatoirement fournir votre mot de passe actuel.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                Laissez ces champs vides si vous ne souhaitez pas changer votre mot de passe
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Champ "Mot de passe actuel" uniquement pour les admins */}
              {user?.role === 'ADMIN' && (
                <div className="sm:col-span-2">
                  <label htmlFor="current_password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Mot de passe actuel
                    {(formData.password || formData.email !== user?.email) && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  <input
                    type="password"
                    id="current_password"
                    name="current_password"
                    value={formData.current_password}
                    onChange={handleChange}
                    placeholder="Votre mot de passe actuel"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Requis pour changer votre mot de passe ou votre email
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Nouveau mot de passe
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  minLength="8"
                  placeholder="Minimum 8 caractères"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label htmlFor="password_confirm" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Confirmer le mot de passe
                </label>
                <input
                  type="password"
                  id="password_confirm"
                  name="password_confirm"
                  value={formData.password_confirm}
                  onChange={handleChange}
                  placeholder="Confirmez le mot de passe"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Préférences RGPD - En lecture seule */}
          <div className="border-t pt-4 sm:pt-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Consentements RGPD</h2>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Ces consentements ont été donnés lors de votre inscription et ne peuvent pas être modifiés.
            </p>
            <div className="space-y-3 sm:space-y-4 bg-gray-50 p-3 sm:p-4 rounded-md">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="accepts_terms"
                  name="accepts_terms"
                  checked={formData.accepts_terms}
                  disabled
                  className="mt-0.5 sm:mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded bg-gray-100 cursor-not-allowed flex-shrink-0"
                />
                <label htmlFor="accepts_terms" className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">
                  <span className="font-medium">
                    J'accepte les conditions générales d'utilisation
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="accepts_privacy_policy"
                  name="accepts_privacy_policy"
                  checked={formData.accepts_privacy_policy}
                  disabled
                  className="mt-0.5 sm:mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded bg-gray-100 cursor-not-allowed flex-shrink-0"
                />
                <label htmlFor="accepts_privacy_policy" className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">
                  <span className="font-medium">
                    J'accepte la politique de confidentialité
                  </span>
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="accepts_data_processing"
                  name="accepts_data_processing"
                  checked={formData.accepts_data_processing}
                  disabled
                  className="mt-0.5 sm:mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded bg-gray-100 cursor-not-allowed flex-shrink-0"
                />
                <label htmlFor="accepts_data_processing" className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-700">
                  <span className="font-medium">
                    J'accepte le traitement de mes données personnelles
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="border-t pt-4 sm:pt-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 active:bg-gray-100 transition-colors text-sm sm:text-base"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </form>

        {/* Actions RGPD */}
        <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Droits RGPD</h3>
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleExportData}
              className="w-full flex items-center justify-center px-4 py-2.5 sm:py-3 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 active:bg-blue-100 transition-colors text-xs sm:text-sm"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Télécharger mes données personnelles
            </button>

            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="w-full flex items-center justify-center px-4 py-2.5 sm:py-3 border border-red-300 text-red-700 rounded-md hover:bg-red-50 active:bg-red-100 transition-colors text-xs sm:text-sm"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Supprimer mon compte
            </button>
          </div>
        </div>

        {/* Informations sur le compte */}
        <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Informations du compte</h3>
          <div className="text-xs sm:text-sm text-gray-600 space-y-1">
            <p>Rôle : <span className="font-medium">{user?.role === 'ADMIN' ? 'Administrateur' : 'Utilisateur'}</span></p>
            <p>Date d'inscription : <span className="font-medium">
              {user?.date_joined ? new Date(user.date_joined).toLocaleDateString('fr-FR') : '-'}
            </span></p>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full my-4 sm:my-8">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Confirmer la suppression du compte</h3>

            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-xs sm:text-sm text-red-700">
                <strong>Attention :</strong> Cette action est irréversible. Toutes vos données seront définitivement supprimées, y compris vos enfants et vos inscriptions.
              </p>
            </div>

            {deleteError && (
              <div className="mb-3 sm:mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-xs sm:text-sm">
                {deleteError}
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="delete_password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Entrez votre mot de passe pour confirmer
              </label>
              <input
                type="password"
                id="delete_password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Mot de passe"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                autoFocus
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteError('');
                }}
                disabled={deleting}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50 text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-colors text-sm"
              >
                {deleting ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Profil;
